/* import __COLOCATED_TEMPLATE__ from './cost-code-input.hbs'; */
import Component from '@glimmer/component';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { inject as service } from '@ember/service';
import { makeMoney } from 'sh-www/helpers/make-money';

const CLASS_NAME = 'SH-c-cost-code-input';

const CATEGORY_ADMIN_ONLY = 'Admin-only';
const CATEGORY_DEACTIVATED = 'Deactivated';
const CATEGORY_MEMBER = 'Member';
const CATEGORY_UNASSIGNED = 'Unassigned';

const DEFAULT_NONE_TEXT = '- None -';

const TYPE_COST_CODES = 'Cost';
const TYPE_STORY_CODES = 'Story';

/**
 * Sorting functions
 */

const sortByKey = (array) => {
  return array.sort((a, b) => a.key.localeCompare(b.key));
}

const sortByKeyAndRemoveCategory = (array) => {
  return array.map(item => {
    const { category, ...rest } = item;
    return rest;
  }).sort((a, b) => a.key.localeCompare(b.key));
}

const sortByCategoryAndKey = (array) => {
  return array.sort((a, b) => {
    if (a.category === b.category) {
      return a.key.localeCompare(b.key);
    } else {
      return a.category.localeCompare(b.category);
    }
  });
}

/**
 * Grouping functions
 */
const groupByHavingBalance = (array) => {
  return array.reduce(
    (accum, item) => {
      if (item.model.availableCreditInCents > 0) {
        accum.withBalance.push(item);
      } else {
        accum.withoutBalance.push(item);
      }

      return accum;
    },
    { withBalance: [], withoutBalance: [] }
  );
}

class CostCodeInput extends Component {
  @service session;

  type = this.args.costCodes ? TYPE_COST_CODES : TYPE_STORY_CODES;

  #options = createCache(() => {
    const {
      showNoneOption,
      showBalances,
      showDefault,
      showDeactivated,
      showPendingFallback,
      costCodes,
      storyCodes,
    } = this.args;

    const options = costCodes || storyCodes;
    return this.sortCodesBySection({
      codes: options,
      type: this.type,
      showNoneOption,
      showBalances,
      showDefault,
      showDeactivated,
      showPendingFallback,
      filterRequireBalance: this.args.filterRequireBalance,
    });
  });

  get options() {
    return getValue(this.#options);
  }

  get className() {
    return CLASS_NAME;
  }

  get company() {
    return this.args.company ?? this.session.currentUser.company;
  }

  get actorUser() {
    return this.args.actorUser ?? this.session.currentUser;
  }

  get currentUserIsAdmin() {
    const { currentUser } = this.session;
    return currentUser.isAdmin || this.actorUser.isCompanyAdmin;
  }

  get altKey() {
    const { value } = this.args;
    return this.options.find((option) => option.key === value)?.altKey ?? '';
  }

  get value() {
    return this.args.value ?? this.args.noneText ?? DEFAULT_NONE_TEXT;
  }

  get teamsInUse() {
    const codes = this.args.costCodes || this.args.storyCodes;
    const teamsUtilized = codes.some(({ companyTeams }) => companyTeams.length > 0);
    return this.company.canUseCompanyTeams && teamsUtilized;
  }

  sortCodesBySection({
    codes,
    type,
    showNoneOption = false,
    showBalances = false,
    showDefault = true,
    showDeactivated = false,
    showPendingFallback = false,
    filterRequireBalance = false,
  }) {
    const { actorUser, currentUserIsAdmin, teamsInUse } = this;

    const adminOnlyOptions = [];
    const unassignedOptions = [];
    const teamOptions = [];
    const deactivatedOptions = [];
    let options = [];

    if (showNoneOption) {
      options.push({
        key: this.args.noneText ?? DEFAULT_NONE_TEXT,
        model: null,
      });
    }

    const filteredCodes = codes.filter((code) => {
      // hide admin codes from non-admins
      const lacksPermission = !currentUserIsAdmin && code.adminOnly;
      if (lacksPermission) {
        return false;
      }

      // remove default for TYPE_COST_CODES when showDefault is false
      const defaultNeedsRemoval = !showDefault && code.default
      if (defaultNeedsRemoval) {
        return false;
      }

      // remove deactivated when showDeactivated is false
      const shouldHideDeactivated = !showDeactivated && code.deactivated;
      if (shouldHideDeactivated) {
        return false;
      }

      return true;
    });

    filteredCodes.forEach((code) => {
      // cost code only conditions
      let altKey = null;
      if (showBalances && code.isCostCode) {
        const availableBalance = code.availableCreditInCents;
        const pendingBalance = code.pendingCreditInCents;

        // requires available balance
        if (filterRequireBalance && !availableBalance) {
          return;
        }

        if (availableBalance) {
          altKey = `${makeMoney([(availableBalance / 100).toFixed(2)])} available`;
        } else if (pendingBalance && showPendingFallback) {
          altKey = `<i>${makeMoney([(pendingBalance / 100).toFixed(2)])} pending</i>`;
        }
      }

      if (code.adminOnly && currentUserIsAdmin) {
        adminOnlyOptions.push({
          key: code.body,
          model: code,
          category: CATEGORY_ADMIN_ONLY,
          icon: 'lock',
          altKey,
        });
      }

      else if (code.deactivated && showDeactivated) {
        deactivatedOptions.push({
          key: code.body,
          model: code,
          category: CATEGORY_DEACTIVATED,
          altKey,
        });
      }

      else if (teamsInUse && code.companyTeams.length > 0) {
        code.companyTeams.forEach(team => {
          if (currentUserIsAdmin || actorUser.companyTeams.find(({ id }) => team.id === id)) {
            teamOptions.push({
              key: code.body,
              model: code,
              category: team.name,
              altKey,
            });
          }
        });
      }

      else if (teamsInUse) {
        unassignedOptions.push({
          key: code.body,
          model: code,
          category: CATEGORY_UNASSIGNED,
          altKey,
        });
      }

      else {
        unassignedOptions.push({
          key: code.body,
          model: code,
          category: `${CATEGORY_MEMBER} ${type} Codes`,
          altKey,
        });
      }
    });

    if (adminOnlyOptions.length || teamOptions.length || teamsInUse || deactivatedOptions.length) {
      // 1. Arrange by category (adminOnly, unassigned, teams, deactivated alphabetically)
      // 2. Sort each category key alphabetically
      if (showBalances) {
        const groupedAdminOnlyOptions = groupByHavingBalance(adminOnlyOptions);
        const groupedUnassignedOptions = groupByHavingBalance(unassignedOptions);
        const groupedTeamOptions = groupByHavingBalance(teamOptions);
        const groupedDeactivatedOptions = groupByHavingBalance(deactivatedOptions);

        options = options.concat(
          sortByKey(groupedAdminOnlyOptions.withBalance),
          sortByKey(groupedAdminOnlyOptions.withoutBalance),
          sortByKey(groupedUnassignedOptions.withBalance),
          sortByKey(groupedUnassignedOptions.withoutBalance),
          sortByCategoryAndKey(groupedTeamOptions.withBalance),
          sortByCategoryAndKey(groupedTeamOptions.withoutBalance),
          sortByKey(groupedDeactivatedOptions.withoutBalance),
          sortByKey(groupedDeactivatedOptions.withoutBalance),
        );
      } else {
        options = options.concat(
          sortByKey(adminOnlyOptions),
          sortByKey(unassignedOptions),
          sortByCategoryAndKey(teamOptions),
          sortByKey(deactivatedOptions),
        );
      }
    } else {
      // Do not show "Member {type} Codes" section if it is the only one
      if (showBalances) {
        const groupedUnassignedOptions = groupByHavingBalance(unassignedOptions);

        options = options.concat(
          sortByKeyAndRemoveCategory(groupedUnassignedOptions.withBalance),
          sortByKeyAndRemoveCategory(groupedUnassignedOptions.withoutBalance),
        );
      } else {
        options = options.concat(
          sortByKeyAndRemoveCategory(unassignedOptions),
        );
      }
    }

    return options;
  }
}

export default CostCodeInput;
